import React, {ReactNode} from "react";
import {Button} from "primereact/button";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import AbstractDialog, {
	AbstractDialogProperties,
	AbstractDialogState
} from "../../../shared/dialog/components/AbstractDialog";
import AdminDialogType from "../AdminDialogType";
import AdminContext from "../../AdminContext";
import {
	Information,
	Parameter, UserCredentials
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Immutable from "../../../shared/immutable/Immutable";
import Exception from "../../../shared/exception/Exception";
import ErrorCode from "../../../shared/exception/ErrorCode";
import Translate from "../../../shared/translation/Translate";
import FunctionHelper from "../../../shared/helper/FunctionHelper";

export interface AdminDialogProperties<PARAMETER_TYPE extends Parameter> extends AbstractDialogProperties, I18nGlobals {
	parameter?: PARAMETER_TYPE;
	context: AdminContext;
}

export interface AdminDialogState<PARAMETER_TYPE extends Parameter> extends AbstractDialogState {
	parameter?: PARAMETER_TYPE;
	originalParameter?: PARAMETER_TYPE;
	fieldErrors: Map<any, string>;
	hasChanges: boolean;
}

export default abstract class AdminDialog<PARAMETER_TYPE extends Parameter = any,
	PROPERTY_TYPE extends AdminDialogProperties<PARAMETER_TYPE> = any,
	STATUS_TYPE extends AdminDialogState<PARAMETER_TYPE> = any> extends AbstractDialog<PROPERTY_TYPE, STATUS_TYPE> {
	/**
	 * Initialize an AbstractDialog component instance
	 */
	constructor(props: Readonly<PROPERTY_TYPE>) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<any, string>()
		} as STATUS_TYPE;

		this.hide = this.hide.bind(this);
		this.onShow = this.onShow.bind(this);
		this.onError = this.onError.bind(this);
		this.renderError = this.renderError.bind(this);
		this.renderInfo = this.renderInfo.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
	}

	/**
	 * Check if the dialog is enabled
	 */
	public static isEnabled(portalInfo: Information): boolean {
		return true;
	}

	/**
	 * Check if the dialog is active for the given documents
	 */
	public static isActive(portalInfo: Information, user?: UserCredentials): boolean {
		return this.isEnabled(portalInfo) && FunctionHelper.applyValue(
			user?.isAdmin, UserCredentials.getIsAdminDefault()
		);
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		await super.submit();
	}

	protected async reset(): Promise<void> {
		this.state.fieldErrors.clear();

		this.setState({
			exception: undefined,
			info: undefined,
			hasChanges: false,
			parameter: this.state.originalParameter?.clone()
		});
	}

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(): void {
		this.state.fieldErrors.clear();

		this.setState({
			submitted: false,
			exception: undefined,
			info: undefined,
			hasChanges: false
		});

		this.props.hide();
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.reset")} icon={"icon icon-rotate-270 small"}
							disabled={!this.state.hasChanges || this.state.submitted}
							className="reset-button" onClick={() => this.reset()}/>
					<Button label={Translate.value("buttons.save")} disabled={!this.isValid()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		let errors: Map<any, string> = this.state.fieldErrors;
		return this.state.hasChanges && errors.size === 0 && !this.state.submitted;
	}

	protected onChange(field: any, value: any): void {
		this.setState({
			hasChanges: this.hasChanges()
		});
	}

	protected hasChanges(): boolean {
		if (!FunctionHelper.exists(this.state.originalParameter)) {
			return FunctionHelper.exists(this.state.parameter);
		}

		let originalParameter: Immutable<PARAMETER_TYPE> = Immutable.from(this.state.originalParameter?.toJson());
		let parameter: Immutable<PARAMETER_TYPE> = Immutable.from(this.state.parameter?.toJson());
		return !parameter.equals(originalParameter);
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (event.key.toLowerCase() === "enter" && this.isValid() && !this.state.submitted) {
			await this.submit();
		}
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await new Promise((resolve) => {
			this.setState({
				parameter: this.props.parameter?.clone(),
				originalParameter: this.props.parameter?.clone()
			}, () => {
				resolve(undefined);
			});
		})
	};

	/**
	 * Handles a dialog exception and returns if the operation can be retried
	 */
	protected async onError(exception: Error): Promise<boolean> {
		let portalException: Exception = Exception.parseException(exception);

		if (portalException.name === "ERR_NETWORK") {
			await this.props.context.session.reconnectServer();
			return false;
		}

		switch (portalException.code) {
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_FOUND:
			case ErrorCode.WEBSERVICE_SESSION_EXPIRED:
			case ErrorCode.WEBSERVICE_SESSION_UNABLE_TO_CREATE:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_ALLOWED:
			case ErrorCode.WEBSERVICE_SESSION_UNKNOWN_ERROR:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_PROVIDED:
				window.location.reload();
				break;
			default:
				return super.onError(exception);
		}

		return false;
	}

	/**
	 * render component as react node
	 */
	abstract renderDialog(): ReactNode;

	/**
	 * Returns the dialog type
	 */
	protected abstract getDialogType(): AdminDialogType;

	/**
	 * Returns the dialog parameters
	 */
	protected getDefaultParameter(): PARAMETER_TYPE | undefined {
		return undefined;
	};
}